/**
 * 7-Day Onboarding Content
 * Each day has a theme designed to create AHA moments, build habits,
 * train focus, and drive license upgrade conversion on day 7.
 */

export const ONBOARDING_DAYS = [
  // ── Day 1: The Breath That Changes Everything ──────────────────────────────
  {
    day: 1,
    icon: "🌬️",
    theme: "breathing",
    accentColor: "#2ABFCF",
    titleNL: "Dag 1 — Jouw reis begint hier",
    titleEN: "Day 1 — Your journey starts here",
    bodyNL:
      "Welkom bij Sense Lifting. In 7 dagen ga je ontdekken hoe kleine gewoontes een grote impact maken.\n\nBegin vandaag met één ademhalingsoefening. Voel het verschil direct — je hersenen kalmeren in minder dan 60 seconden.",
    bodyEN:
      "Welcome to Sense Lifting. In 7 days you'll discover how small habits create big impact.\n\nStart today with one breathing exercise. Feel the difference immediately — your brain calms down in under 60 seconds.",
    ctaNL: "Doe nu een ademhalingsoefening",
    ctaEN: "Do a breathing exercise now",
    ctaTab: 2,
    ahaLabelNL: "💡 AHA-moment",
    ahaLabelEN: "💡 AHA Moment",
    ahaNL: "Bewust ademen activeert je parasympathisch zenuwstelsel — je lichaam's eigen \"reset\" knop.",
    ahaEN: "Conscious breathing activates your parasympathetic nervous system — your body's own \"reset\" button.",
    notifNL: "Je eerste dag bij Sense Lifting 🌬️ — doe vandaag je eerste ademhalingsoefening en voel het verschil direct.",
    notifEN: "Your first day at Sense Lifting 🌬️ — do your first breathing exercise today and feel the difference immediately.",
  },

  // ── Day 2: Your Brain Runs on Sleep ────────────────────────────────────────
  {
    day: 2,
    icon: "🌙",
    theme: "sleep",
    accentColor: "#7B6FD0",
    titleNL: "Dag 2 — Jouw brein draait op slaap",
    titleEN: "Day 2 — Your brain runs on sleep",
    bodyNL:
      "Wist je dat slechts één slechte nacht je concentratievermogen met 30% verlaagt?\n\nSense Lifting meet jouw slaapkwaliteit en koppelt dit direct aan jouw mentale laad-score. Log vandaag je slaap en zie hoe het jouw dag beïnvloedt.",
    bodyEN:
      "Did you know just one bad night reduces your concentration by 30%?\n\nSense Lifting measures your sleep quality and links it directly to your mental load score. Log your sleep today and see how it impacts your day.",
    ctaNL: "Slaap loggen",
    ctaEN: "Log my sleep",
    ctaTab: 8,
    ahaLabelNL: "💡 AHA-moment",
    ahaLabelEN: "💡 AHA Moment",
    ahaNL: "Jouw slaap van gisternacht bepaalt vandaag hoeveel mentale energie je hebt voor moeilijke taken.",
    ahaEN: "Last night's sleep determines how much mental energy you have today for difficult tasks.",
    notifNL: "Dag 2 💜 — Hoe heb je geslapen? Log je slaap en ontdek hoe het jouw focus beïnvloedt.",
    notifEN: "Day 2 💜 — How did you sleep? Log your sleep and discover how it shapes your focus.",
  },

  // ── Day 3: Notice Your Emotions ────────────────────────────────────────────
  {
    day: 3,
    icon: "🌊",
    theme: "mood",
    accentColor: "#4AC87A",
    titleNL: "Dag 3 — Merk jouw emoties op",
    titleEN: "Day 3 — Notice your emotions",
    bodyNL:
      "Na 3 dagen beginnen patronen zich af te tekenen. Jouw stemming is geen toeval — het is informatie.\n\nMensen die hun stemming bijhouden, rapporteren 40% minder onverwachte stresspieken. Jij bent al onderweg.",
    bodyEN:
      "After 3 days, patterns are starting to emerge. Your mood is not random — it's information.\n\nPeople who track their mood report 40% fewer unexpected stress spikes. You're already on your way.",
    ctaNL: "Mijn stemming loggen",
    ctaEN: "Log my mood",
    ctaTab: 8,
    ahaLabelNL: "💡 AHA-moment",
    ahaLabelEN: "💡 AHA Moment",
    ahaNL: "Stemming bijhouden creëert bewustzijn. Bewustzijn is de eerste stap naar controle.",
    ahaEN: "Tracking mood creates awareness. Awareness is the first step toward control.",
    notifNL: "Dag 3 🌊 — Jouw patronen worden zichtbaar. Hoe voel jij je vandaag? Log het nu.",
    notifEN: "Day 3 🌊 — Your patterns are becoming visible. How do you feel today? Log it now.",
  },

  // ── Day 4: Stronger Together ───────────────────────────────────────────────
  {
    day: 4,
    icon: "🤝",
    theme: "social",
    accentColor: "#D4873A",
    titleNL: "Dag 4 — Samen ben je sterker",
    titleEN: "Day 4 — Stronger together",
    bodyNL:
      "Onderzoek toont aan dat mensen 65% consistenter zijn als ze trainingsgewoontes delen met anderen.\n\nNodig een vriend of collega uit op Sense Lifting. Doe oefeningen samen, zie elkaars voortgang en houd elkaar gemotiveerd.",
    bodyEN:
      "Research shows people are 65% more consistent when they share training habits with others.\n\nInvite a friend or colleague to Sense Lifting. Do exercises together, see each other's progress, and keep each other motivated.",
    ctaNL: "Gemeenschap verkennen",
    ctaEN: "Explore community",
    ctaTab: 7,
    ahaLabelNL: "💡 AHA-moment",
    ahaLabelEN: "💡 AHA Moment",
    ahaNL: "Sociale verbinding is zelf een oefening voor je brein. Jouw sociale vaardigheidsscore wordt hier bijgehouden.",
    ahaEN: "Social connection is itself an exercise for your brain. Your social skill score is tracked right here.",
    notifNL: "Dag 4 🤝 — Train niet alleen. Nodig vandaag iemand uit en word 65% consistenter.",
    notifEN: "Day 4 🤝 — Don't train alone. Invite someone today and become 65% more consistent.",
  },

  // ── Day 5: Balance is a Skill ──────────────────────────────────────────────
  {
    day: 5,
    icon: "⚖️",
    theme: "planning",
    accentColor: "#C8A84B",
    titleNL: "Dag 5 — Balans is een vaardigheid",
    titleEN: "Day 5 — Balance is a skill",
    bodyNL:
      "Halverwege jouw eerste week. Kijk nu naar je weekplanning — hoeveel mentale versus fysieke oefeningen heb je gepland?\n\nEen ongeplande week leidt tot uitputting. Een gebalanceerde week leidt tot groei. Sense Lifting toont je de balans in real-time.",
    bodyEN:
      "Halfway through your first week. Look at your week plan now — how many mental vs physical exercises have you planned?\n\nAn unplanned week leads to burnout. A balanced week leads to growth. Sense Lifting shows you the balance in real-time.",
    ctaNL: "Mijn weekplan bekijken",
    ctaEN: "Review my week plan",
    ctaTab: 1,
    ahaLabelNL: "💡 AHA-moment",
    ahaLabelEN: "💡 AHA Moment",
    ahaNL: "Jouw burnout-score stijgt als je mentale en fysieke belasting niet in balans zijn. Nu zie je dit voordat het te laat is.",
    ahaEN: "Your burnout score rises when mental and physical load are out of balance. Now you see it before it's too late.",
    notifNL: "Dag 5 ⚖️ — Halverwege jouw eerste week. Is jouw plan in balans? Bekijk het nu.",
    notifEN: "Day 5 ⚖️ — Halfway through your first week. Is your plan balanced? Check it now.",
  },

  // ── Day 6: See How Far You've Come ─────────────────────────────────────────
  {
    day: 6,
    icon: "📈",
    theme: "progress",
    accentColor: "#4AC87A",
    titleNL: "Dag 6 — Kijk hoe ver je al bent",
    titleEN: "Day 6 — Look how far you've come",
    bodyNL:
      "Zes dagen geleden begon jij. Kijk nu wat je hebt opgebouwd: slaapdata, stemmingspatronen, oefeningen, sociale connecties.\n\nDit is nog maar het begin. Stel je voor wat 30 dagen consistent doen voor jou kan betekenen.",
    bodyEN:
      "Six days ago you started. Look at what you've built: sleep data, mood patterns, exercises, social connections.\n\nThis is just the beginning. Imagine what 30 days of consistency can do for you.",
    ctaNL: "Mijn voortgang zien",
    ctaEN: "See my progress",
    ctaTab: 0,
    ahaLabelNL: "💡 AHA-moment",
    ahaLabelEN: "💡 AHA Moment",
    ahaNL: "Consistentie over 6 dagen is moeilijker dan de meeste mensen denken. Jij hebt het gedaan.",
    ahaEN: "Consistency over 6 days is harder than most people think. You did it.",
    notifNL: "Dag 6 📈 — Kijk eens hoe ver je al bent gekomen. Jouw data vertelt jouw verhaal.",
    notifEN: "Day 6 📈 — Look how far you've already come. Your data tells your story.",
  },

  // ── Day 7: The Foundation is Set — Upgrade ─────────────────────────────────
  {
    day: 7,
    icon: "🚀",
    theme: "upgrade",
    accentColor: "#C8A84B",
    titleNL: "Dag 7 — De basis staat. Nu ga je verder.",
    titleEN: "Day 7 — The foundation is set. Now go deeper.",
    bodyNL:
      "Jij hebt 7 dagen lang ervaren wat Sense Lifting voor je kan doen. Ademhaling. Slaap. Stemming. Balans. Verbinding.\n\nMet een maandabonnement unlock je:\n• Onbeperkte Sense Lifting Coach gesprekken\n• Geavanceerde statistieken & trends\n• Push herinneringen op jouw ideale momenten\n• Exporteer jouw data\n• En veel meer",
    bodyEN:
      "You've experienced what Sense Lifting can do for you over 7 days. Breathing. Sleep. Mood. Balance. Connection.\n\nWith a monthly subscription you unlock:\n• Unlimited Sense Lifting Coach conversations\n• Advanced statistics & trends\n• Push reminders at your ideal moments\n• Export your data\n• And much more",
    ctaNL: "Upgrade naar Individual — €7,99/maand",
    ctaEN: "Upgrade to Individual — €7.99/month",
    ctaTab: null,
    ctaAction: "upgrade",
    isUpgradeDay: true,
    ahaLabelNL: "🎯 Jouw 7-daagse resultaten",
    ahaLabelEN: "🎯 Your 7-day results",
    ahaNL: "In 7 dagen heb je een bewustzijn opgebouwd dat de meeste mensen nooit krijgen. Bouw hierop voort.",
    ahaEN: "In 7 days you've built an awareness most people never develop. Build on this.",
    notifNL: "Dag 7 🚀 — Jij hebt de eerste week voltooid. Ontgrendel nu de volledige kracht van Sense Lifting.",
    notifEN: "Day 7 🚀 — You completed your first week. Now unlock the full power of Sense Lifting.",
    secondaryCtaNL: "Misschien later",
    secondaryCtaEN: "Maybe later",
  },
];

/** Returns 0-based day index (0 = day 1) or null if outside 7-day window */
export function getOnboardingDay(signedUpAt) {
  if (!signedUpAt) return null;
  try {
    const start = new Date(signedUpAt);
    const now   = new Date();
    // Zero out time components for day diff
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const nowDay   = new Date(now.getFullYear(),   now.getMonth(),   now.getDate());
    const diffMs   = nowDay - startDay;
    const diffDays = Math.floor(diffMs / 86400000); // 0 on day 1
    if (diffDays < 0 || diffDays > 6) return null;
    return diffDays; // 0–6
  } catch {
    return null;
  }
}
